import { SUBSCRIPTION, type Subscription } from '@algoacademy/backend/modules/marketplace/enums'
import type { MentoringDiscount } from '@algoacademy/backend/modules/payment/discount-pricing'
import { PAYMENT__DISCOUNT_SHAPE } from '@algoacademy/backend/modules/payment/enums'
import type { MentoringDiscountLadder } from '../_components/DiscountControls/_helpers/discount-ladder'
import {
  formatBillingPeriodCount,
  formatZloty
} from '../_forms/MentoringOfferForm/_helpers/mentoring-terms'
import { roundUpToNearestZloty } from '../prices'

export type BuyDataStruckPrice = {
  zloty: number
  cadenceBadgePercent: number | null
  staffDiscountLabel: string | null
}

export type BuyDataPricingTableRow = {
  kind: 'discounted' | 'full' | 'endless-total' | 'savings'
  label: string
  amountZloty: string
  /** null when the cadence is already shown above the table, e.g. on 4-week billing. */
  cadenceSuffix: string | null
  green?: boolean
}

export type BuyDataDisplayModel = {
  struckPrices: BuyDataStruckPrice[]
  finalPriceZloty: number
  pricingTable: BuyDataPricingTableRow[]
}

type SubscriptionPriceCalculation = {
  discountedPrice: number
  savings: number
  periodsCount: number
}

type ReadBuyDataDisplayModelArgs = {
  subscription: Subscription
  discount: MentoringDiscount | null
  ladder: MentoringDiscountLadder | null
  originalPerFourWeeks: number
  discountedPerFourWeeks: number
  discountPercent: number
  priceCalculation: SubscriptionPriceCalculation
}

/** Slash cadence suffix for invoice totals, e.g. "/ 12 tygodni". */
export function readBillingCadenceSlashSuffix(subscription: Subscription): string {
  switch (subscription) {
    case SUBSCRIPTION.PER_4_WEEKS:
      return '/ 4 tygodnie'
    case SUBSCRIPTION.PER_12_WEEKS:
      return '/ 12 tygodni'
    case SUBSCRIPTION.YEARLY:
      return '/ rok'
    default: {
      const _exhaustive: never = subscription
      return _exhaustive
    }
  }
}

function showsCadencePricingSummary(subscription: Subscription): boolean {
  return subscription === SUBSCRIPTION.PER_12_WEEKS || subscription === SUBSCRIPTION.YEARLY
}

function readPricingTableRowAmount(
  zloty: number,
  subscription: Subscription
): Pick<BuyDataPricingTableRow, 'amountZloty' | 'cadenceSuffix'> {
  if (subscription === SUBSCRIPTION.PER_4_WEEKS) {
    return { amountZloty: formatZloty(zloty), cadenceSuffix: null }
  }

  return {
    amountZloty: formatZloty(zloty),
    cadenceSuffix: readBillingCadenceSlashSuffix(subscription)
  }
}

function readEndlessSavingsRow(
  savedPerBilling: number,
  subscription: Subscription
): BuyDataPricingTableRow {
  return {
    kind: 'savings',
    label: 'Oszczędzasz',
    amountZloty: formatZloty(savedPerBilling),
    cadenceSuffix: readBillingCadenceSlashSuffix(subscription),
    green: true
  }
}

function formatLimitedDiscountFirstPeriodsLabel(periodCount: number): string {
  if (periodCount === 1) {
    return 'Przez pierwszy okres'
  }

  return `Przez pierwsze ${formatBillingPeriodCount(periodCount)}`
}

function formatLimitedDiscountFromPeriodLabel(fromPeriod: number): string {
  return `Od ${fromPeriod} okresu`
}

function readPublicCadencePricingTable(args: {
  subscription: Subscription
  priceCalculation: SubscriptionPriceCalculation
}): BuyDataPricingTableRow[] {
  const { subscription, priceCalculation } = args
  const { discountedPrice, savings } = priceCalculation

  const rows: BuyDataPricingTableRow[] = [
    {
      kind: 'endless-total',
      label: 'Łącznie',
      ...readPricingTableRowAmount(discountedPrice, subscription)
    }
  ]

  if (savings > 0) {
    rows.push({
      kind: 'savings',
      label: 'Oszczędzasz',
      ...readPricingTableRowAmount(savings, subscription),
      green: true
    })
  }

  return rows
}

/** Staff discount label on the struck price ladder. Amount is spread to 4 weeks, rounded up. */
export function formatStaffDiscountLabel(args: {
  discount: MentoringDiscount
  periodsPerInvoice: number
}): string {
  const { discount, periodsPerInvoice } = args

  if (discount.shape === PAYMENT__DISCOUNT_SHAPE.PERCENT) {
    return `-${discount.value}%`
  }

  const perFourWeeksZloty = roundUpToNearestZloty(discount.value / 100 / periodsPerInvoice)

  return `-${formatZloty(perFourWeeksZloty)}`
}

/** Struck prices from list → cadence → staff, with badges on the right step. */
export function readBuyDataStruckPrices(args: {
  discount: MentoringDiscount | null
  originalPerFourWeeks: number
  discountedPerFourWeeks: number
  discountPercent: number
  periodsPerInvoice: number
}): BuyDataStruckPrice[] {
  const {
    discount,
    originalPerFourWeeks,
    discountedPerFourWeeks,
    discountPercent,
    periodsPerInvoice
  } = args
  const staffLabel =
    discount === null ? null : formatStaffDiscountLabel({ discount, periodsPerInvoice })
  const cadenceBadge = discountPercent > 0 ? discountPercent : null

  if (discount === null) {
    if (cadenceBadge === null) return []

    return [
      { zloty: originalPerFourWeeks, cadenceBadgePercent: cadenceBadge, staffDiscountLabel: null }
    ]
  }

  if (cadenceBadge === null) {
    return [
      { zloty: originalPerFourWeeks, cadenceBadgePercent: null, staffDiscountLabel: staffLabel }
    ]
  }

  return [
    { zloty: originalPerFourWeeks, cadenceBadgePercent: cadenceBadge, staffDiscountLabel: null },
    {
      zloty: discountedPerFourWeeks,
      cadenceBadgePercent: null,
      staffDiscountLabel: staffLabel
    }
  ]
}

/** Invoice breakdown and savings under the 4-week price when staff pricing is active. */
export function readBuyDataPricingTable(
  args: ReadBuyDataDisplayModelArgs
): BuyDataPricingTableRow[] {
  const { subscription, discount, ladder, priceCalculation } = args

  if (discount === null || ladder === null) {
    if (!showsCadencePricingSummary(subscription)) {
      return []
    }

    return readPublicCadencePricingTable({ subscription, priceCalculation })
  }

  if (ladder.totals.kind === 'endless') {
    const savedPerBilling = ladder.totals.savedPerBillingPeriodZloty

    if (savedPerBilling <= 0) {
      return []
    }

    if (subscription === SUBSCRIPTION.PER_4_WEEKS) {
      return [readEndlessSavingsRow(savedPerBilling, subscription)]
    }

    return [
      {
        kind: 'endless-total',
        label: 'Łącznie',
        ...readPricingTableRowAmount(ladder.discountedInvoiceZloty, subscription)
      },
      readEndlessSavingsRow(savedPerBilling, subscription)
    ]
  }

  const { periodCount, savedZloty } = ladder.totals

  if (savedZloty <= 0) {
    return []
  }

  return [
    {
      kind: 'discounted',
      label: formatLimitedDiscountFirstPeriodsLabel(periodCount),
      ...readPricingTableRowAmount(ladder.discountedInvoiceZloty, subscription)
    },
    {
      kind: 'full',
      label: formatLimitedDiscountFromPeriodLabel(periodCount + 1),
      ...readPricingTableRowAmount(ladder.listInvoiceZloty, subscription)
    },
    {
      kind: 'savings',
      label: 'Łącznie oszczędzasz',
      amountZloty: formatZloty(savedZloty),
      cadenceSuffix: null,
      green: true
    }
  ]
}

export function readBuyDataDisplayModel(args: ReadBuyDataDisplayModelArgs): BuyDataDisplayModel {
  const {
    discount,
    ladder,
    originalPerFourWeeks,
    discountedPerFourWeeks,
    discountPercent,
    priceCalculation
  } = args

  const struckPrices = readBuyDataStruckPrices({
    discount,
    originalPerFourWeeks,
    discountedPerFourWeeks,
    discountPercent,
    periodsPerInvoice: priceCalculation.periodsCount
  })

  const finalPriceZloty =
    ladder === null
      ? discountPercent > 0
        ? discountedPerFourWeeks
        : originalPerFourWeeks
      : ladder.chargedPerFourWeeksZloty

  return {
    struckPrices,
    finalPriceZloty,
    pricingTable: readBuyDataPricingTable(args)
  }
}
